import { Component, useCallback, useEffect, useMemo, useRef, useState, type ErrorInfo, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getSeededChapter, type ReaderParagraph } from "@/data/curatedChapters";
import { getLibraryBook, isLibraryBookId } from "@/data/libraryBooks";
import { chapterBodyToReaderParagraphs } from "@/lib/chapterMarkers";
import {
  getImportedChapterWithLegacyFallback,
  listImportedChapterSlugs,
  sortChapterSlugs,
} from "@/lib/importedBookDb";
import {
  chapterConfigsForImport,
  hasAnyImportedBookChapter,
  hasImportedBookFullText,
  readShelfBookById,
} from "@/lib/importedBookStorage";
import { getPublicDomainSearchResultById } from "@/lib/publicDomainSearch";
import { coerceReaderResumePayload } from "@/lib/readerBookmark";
import {
  ImmersiveBookReader,
  type ReaderChapterListEntry,
  type ReaderSearchBlock,
} from "@/pages/app/reader/ImmersiveBookReader";
import { ReaderChapterNav } from "@/pages/app/reader/ReaderChapterNav";

function chapterSlugToLabel(chapterSlug: string): string {
  const m = /^chapter-(\d+)$/.exec(chapterSlug);
  if (m) return `Chapter ${m[1]}`;
  return chapterSlug.replace(/-/g, " ");
}

const btnPrimary =
  "inline-flex items-center justify-center rounded-full bg-[#c9a227] px-5 py-2.5 text-sm font-semibold text-[#1a1308] transition-colors hover:from-[#d4ae32] disabled:cursor-not-allowed disabled:opacity-40";

function LibrarySceneFrame(props: { children: ReactNode }) {
  return (
    <div
      className="min-h-[100dvh] w-full px-4 py-10 sm:px-6"
      style={{
        background:
          "radial-gradient(ellipse 100% 70% at 50% 0%, rgba(201,162,39,0.12) 0%, transparent 50%), linear-gradient(165deg, #1a120c 0%, #261a12 100%)",
      }}
    >
      <div className="mx-auto max-w-3xl text-[#e8dcc8]">{props.children}</div>
    </div>
  );
}

type ReaderBoundaryProps = { children: ReactNode };
type ReaderBoundaryState = { error: Error | null };

/** Last-resort guard so a render bug in the immersive reader does not blank the whole route. */
class ReaderExperienceErrorBoundary extends Component<ReaderBoundaryProps, ReaderBoundaryState> {
  state: ReaderBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ReaderBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ReaderExperienceErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <LibrarySceneFrame>
          <h1 className="text-2xl font-bold text-[#fdf6e9]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Reader could not load
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-[#b9a88a]" style={{ fontFamily: "'Inter', sans-serif" }}>
            A display error stopped the reader. Your book data in this browser is unchanged.
          </p>
          <pre className="mt-4 max-h-48 overflow-auto rounded-md border border-[#5c4a2a]/60 bg-[#0f0c09]/90 p-3 text-left text-xs text-amber-100/90">
            {this.state.error.message}
          </pre>
          <button
            type="button"
            className={`${btnPrimary} mt-8`}
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
          <div className="mt-6">
            <Link className={`${btnPrimary} inline-flex`} to="/app/library">
              Back to Library
            </Link>
          </div>
        </LibrarySceneFrame>
      );
    }
    return this.props.children;
  }
}

export default function ReadChapterPage() {
  const { bookId, chapterId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const readerResume = useMemo(() => {
    try {
      const st = location.state as { readerResume?: unknown } | null | undefined;
      return coerceReaderResumePayload(st?.readerResume);
    } catch {
      return null;
    }
  }, [location.state]);

  const catalogBook = useMemo(() => {
    if (!bookId || !isLibraryBookId(bookId)) return undefined;
    return getLibraryBook(bookId);
  }, [bookId]);

  const shelfBook = useMemo(() => {
    if (!bookId) return null;
    return readShelfBookById(bookId);
  }, [bookId]);

  const mockMeta = useMemo(() => {
    if (!bookId) return undefined;
    return getPublicDomainSearchResultById(bookId);
  }, [bookId]);

  const displayTitle = catalogBook?.title ?? shelfBook?.title ?? mockMeta?.title;
  const displayAuthor = catalogBook?.author ?? shelfBook?.author ?? mockMeta?.author ?? "";

  const seed = useMemo(() => {
    if (!bookId || !chapterId || !isLibraryBookId(bookId)) return null;
    return getSeededChapter(bookId, chapterId);
  }, [bookId, chapterId]);

  const catalogChapterConfigs = useMemo(() => chapterConfigsForImport(mockMeta), [mockMeta]);

  const [importChapterPending, setImportChapterPending] = useState(true);
  const [importedParagraphs, setImportedParagraphs] = useState<ReaderParagraph[] | null>(null);
  const [importedChapterTitle, setImportedChapterTitle] = useState<string | undefined>(undefined);
  const [idbChapterSlugs, setIdbChapterSlugs] = useState<string[]>([]);
  const [bookSearchBlocks, setBookSearchBlocks] = useState<ReaderSearchBlock[]>([]);
  const [bookSearchLoading, setBookSearchLoading] = useState(false);
  const [castAnalysisEligible, setCastAnalysisEligible] = useState(false);

  const canResolveBook = Boolean(displayTitle);
  /** Invalidates in-flight chapter IDB loads so `importChapterPending` cannot stay true after deps change/unmount. */
  const chapterLoadGenRef = useRef(0);

  useEffect(() => {
    if (!bookId) {
      setCastAnalysisEligible(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const full = await hasImportedBookFullText(bookId);
        const any = await hasAnyImportedBookChapter(bookId);
        if (!cancelled) setCastAnalysisEligible(full || any);
      } catch {
        if (!cancelled) setCastAnalysisEligible(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookId]);

  const clearReaderResumeState = useCallback(() => {
    try {
      navigate(".", { replace: true, state: {} });
    } catch {
      /* ignore */
    }
  }, [navigate]);

  useEffect(() => {
    if (!bookId || !canResolveBook) {
      setIdbChapterSlugs([]);
      return;
    }
    void listImportedChapterSlugs(bookId).then(setIdbChapterSlugs);
  }, [bookId, canResolveBook]);

  useEffect(() => {
    if (!bookId || !chapterId || !canResolveBook) {
      setImportChapterPending(false);
      setImportedParagraphs(null);
      setImportedChapterTitle(undefined);
      return;
    }
    const loadGen = ++chapterLoadGenRef.current;
    let cancelled = false;
    setImportChapterPending(true);
    setImportedParagraphs(null);
    setImportedChapterTitle(undefined);
    void (async () => {
      try {
        const { text, title } = await getImportedChapterWithLegacyFallback(bookId, chapterId);
        if (import.meta.env.DEV) {
          console.debug("[ReadChapterPage] chapter load", {
            bookId,
            chapterSlug: chapterId,
            loadGen,
            currentGen: chapterLoadGenRef.current,
            loadedBodyLength: text?.length ?? 0,
          });
        }
        if (cancelled || chapterLoadGenRef.current !== loadGen) return;
        if (typeof text === "string" && text.trim().length > 0) {
          const parsed = chapterBodyToReaderParagraphs(text);
          setImportedParagraphs(parsed.length > 0 ? parsed : null);
        } else {
          setImportedParagraphs(null);
        }
        if (typeof title === "string" && title.trim().length > 0) {
          setImportedChapterTitle(title.trim());
        }
      } catch {
        if (!cancelled && chapterLoadGenRef.current === loadGen) {
          setImportedParagraphs(null);
          setImportedChapterTitle(undefined);
        }
      } finally {
        if (chapterLoadGenRef.current === loadGen) {
          setImportChapterPending(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      chapterLoadGenRef.current += 1;
    };
  }, [bookId, chapterId, canResolveBook]);

  const paragraphs = importedParagraphs ?? seed?.paragraphs ?? null;

  const catalogTitleForSlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of catalogChapterConfigs) {
      if (c.title) map.set(c.chapterSlug, c.title);
    }
    return map;
  }, [catalogChapterConfigs]);

  const navChapterSlugs = useMemo(() => {
    if (catalogChapterConfigs.length > 0) {
      return catalogChapterConfigs.map((c) => c.chapterSlug);
    }
    return sortChapterSlugs(idbChapterSlugs);
  }, [catalogChapterConfigs, idbChapterSlugs]);

  const navChapterSlugsKey = navChapterSlugs.join("\0");

  const chapterEntries = useMemo((): ReaderChapterListEntry[] => {
    try {
      if (!bookId) return [];
      const titleBySlug = new Map<string, string>();
      for (const c of catalogChapterConfigs) {
        if (c.title) titleBySlug.set(c.chapterSlug, c.title);
      }
      return navChapterSlugs.map((slug) => ({
        slug,
        title: titleBySlug.get(slug) ?? getSeededChapter(bookId, slug)?.chapterTitle ?? chapterSlugToLabel(slug),
      }));
    } catch {
      return [];
    }
  }, [bookId, navChapterSlugs, catalogChapterConfigs]);

  useEffect(() => {
    if (!bookId || !canResolveBook || navChapterSlugs.length === 0) {
      setBookSearchBlocks([]);
      setBookSearchLoading(false);
      return;
    }
    let cancelled = false;
    setBookSearchLoading(true);
    void (async () => {
      try {
        const blocks: ReaderSearchBlock[] = [];
        for (const slug of navChapterSlugs) {
          try {
            const seedCh = getSeededChapter(bookId, slug);
            if (seedCh?.paragraphs?.length) {
              seedCh.paragraphs.forEach((p, i) => {
                const label = typeof p?.label === "string" ? p.label : "Narrator";
                const text = typeof p?.text === "string" ? p.text : "";
                blocks.push({ chapterSlug: slug, paragraphIndex: i, label, text });
              });
              continue;
            }
            const { text } = await getImportedChapterWithLegacyFallback(bookId, slug);
            if (cancelled) return;
            if (typeof text === "string" && text.trim().length > 0) {
              const parsed = chapterBodyToReaderParagraphs(text);
              parsed.forEach((p, i) => {
                const label = typeof p?.label === "string" ? p.label : "Narrator";
                const textPart = typeof p?.text === "string" ? p.text : "";
                blocks.push({ chapterSlug: slug, paragraphIndex: i, label, text: textPart });
              });
            }
          } catch {
            /* skip chapter */
          }
        }
        if (!cancelled) setBookSearchBlocks(blocks);
      } catch {
        if (!cancelled) setBookSearchBlocks([]);
      } finally {
        if (!cancelled) setBookSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookId, canResolveBook, navChapterSlugsKey]);

  const navIndex = navChapterSlugs.indexOf(chapterId ?? "");
  const prevSlug = navIndex > 0 ? navChapterSlugs[navIndex - 1] : null;
  const nextSlug = navIndex >= 0 && navIndex < navChapterSlugs.length - 1 ? navChapterSlugs[navIndex + 1] : null;

  const chapterHeading =
    seed?.title ??
    catalogTitleForSlug.get(chapterId ?? "") ??
    importedChapterTitle ??
    (chapterId ? chapterSlugToLabel(chapterId) : "Chapter");

  const hasImportedBody = Boolean(importedParagraphs && importedParagraphs.length > 0);
  const hasReadableLayer = hasImportedBody || Boolean(seed);

  if (!bookId || !chapterId) {
    return (
      <LibrarySceneFrame>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#fdf6e9]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Chapter not found
          </h1>
          <p className="mt-3 text-sm text-[#b9a88a]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Missing book or chapter in the URL.
          </p>
          <Link className={`${btnPrimary} mt-8 inline-flex`} to="/app/library">
            Back to Library
          </Link>
        </div>
      </LibrarySceneFrame>
    );
  }

  if (!canResolveBook) {
    return (
      <LibrarySceneFrame>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#fdf6e9]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Book not found
          </h1>
          <p className="mt-3 text-sm text-[#b9a88a]" style={{ fontFamily: "'Inter', sans-serif" }}>
            This title is not in the reader yet. Add it from Discover, upload a .txt from Upload, or import from My Library.
          </p>
          <Link className={`${btnPrimary} mt-8 inline-flex`} to="/app/library">
            Back to Library
          </Link>
        </div>
      </LibrarySceneFrame>
    );
  }

  if (importChapterPending) {
    return (
      <LibrarySceneFrame>
        <nav className="mb-8 flex flex-wrap items-center gap-2 text-xs text-[#9a8470]" style={{ fontFamily: "'Inter', sans-serif" }}>
          <Link to="/app" className="hover:text-[#f0e6d4]">
            Home
          </Link>
          <span aria-hidden>/</span>
          <Link to="/app/library" className="hover:text-[#f0e6d4]">
            Library
          </Link>
          <span aria-hidden>/</span>
          <span className="font-medium text-[#e8dcc8]">{displayTitle}</span>
        </nav>
        <div
          className="rounded-xl border border-[#5c4a2a]/50 p-12 text-center"
          style={{
            background: "linear-gradient(180deg, rgba(42,31,20,0.9) 0%, rgba(26,18,12,0.95) 100%)",
            boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
          }}
        >
          <p className="text-sm font-medium text-[#e8dcc8]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Loading chapter text…
          </p>
          <p className="mt-2 text-xs text-[#8a7a66]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Checking for imported text in this browser.
          </p>
        </div>
      </LibrarySceneFrame>
    );
  }

  if (!hasReadableLayer) {
    return (
      <LibrarySceneFrame>
        <nav className="mb-8 flex flex-wrap items-center gap-2 text-xs text-[#9a8470]" style={{ fontFamily: "'Inter', sans-serif" }}>
          <Link to="/app" className="hover:text-[#f0e6d4]">
            Home
          </Link>
          <span aria-hidden>/</span>
          <Link to="/app/library" className="hover:text-[#f0e6d4]">
            Library
          </Link>
          <span aria-hidden>/</span>
          <span className="font-medium text-[#e8dcc8]">{displayTitle}</span>
        </nav>
        <div
          className="rounded-xl border border-[#5c4a2a]/50 p-8 text-center"
          style={{
            background: "linear-gradient(180deg, rgba(42,31,20,0.9) 0%, rgba(26,18,12,0.95) 100%)",
            boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
          }}
        >
          <h1 className="text-2xl font-bold text-[#fdf6e9]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {chapterHeading}
          </h1>
          {displayAuthor ? (
            <p className="mt-2 text-sm text-[#c4b49a]" style={{ fontFamily: "'Lora', serif" }}>
              {displayAuthor}
            </p>
          ) : null}
          <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-[#b9a88a]" style={{ fontFamily: "'Inter', sans-serif" }}>
            No text for this chapter yet. If you imported the book, we may still need a chapter slice for this edition;
            curated reader content may also be unavailable for this route.
          </p>
          <div className="mt-8 flex justify-center">
            <ReaderChapterNav bookId={bookId} prevSlug={prevSlug} nextSlug={nextSlug} layout="footer" variant="library" />
          </div>
        </div>
      </LibrarySceneFrame>
    );
  }

  if (!paragraphs || paragraphs.length === 0) {
    return (
      <LibrarySceneFrame>
        <nav className="mb-8 flex flex-wrap items-center gap-2 text-xs text-[#9a8470]" style={{ fontFamily: "'Inter', sans-serif" }}>
          <Link to="/app" className="hover:text-[#f0e6d4]">
            Home
          </Link>
          <span aria-hidden>/</span>
          <Link to="/app/library" className="hover:text-[#f0e6d4]">
            Library
          </Link>
          <span aria-hidden>/</span>
          <span className="font-medium text-[#e8dcc8]">{displayTitle}</span>
        </nav>
        <div
          className="rounded-xl border border-[#5c4a2a]/50 p-8 text-center"
          style={{
            background: "linear-gradient(180deg, rgba(42,31,20,0.9) 0%, rgba(26,18,12,0.95) 100%)",
          }}
        >
          <p className="text-sm text-[#b9a88a]" style={{ fontFamily: "'Inter', sans-serif" }}>
            No reader text is available for this chapter yet.
          </p>
          <div className="mt-8 flex justify-center">
            <ReaderChapterNav bookId={bookId} prevSlug={prevSlug} nextSlug={nextSlug} layout="footer" variant="library" />
          </div>
        </div>
      </LibrarySceneFrame>
    );
  }

  return (
    <ReaderExperienceErrorBoundary key={`${bookId}-${chapterId}`}>
      <ImmersiveBookReader
        bookId={bookId}
        chapterSlug={chapterId}
        bookTitle={displayTitle ?? ""}
        author={displayAuthor}
        chapterHeading={chapterHeading}
        paragraphs={paragraphs}
        chapterList={chapterEntries}
        bookSearchBlocks={bookSearchBlocks}
        bookSearchLoading={bookSearchLoading}
        initialReaderResume={readerResume}
        onReaderResumeConsumed={clearReaderResumeState}
        castAnalysisEligible={castAnalysisEligible}
      />
    </ReaderExperienceErrorBoundary>
  );
}
